import React, { useEffect } from 'react'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import StyledList from '@toasttab/shared/src/components/molecules/StyledList/StyledList'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import { ContentfulImageAPI } from '@toasttab/shared/src/utils/contentfulUtils'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { PricingCardCTAProps } from './types'

const PricingCardCTA = ({
  badgeColor,
  badgeText,
  children, // styledList
  ctaLabel,
  ctaLink,
  packagePrice,
  packagePriceMonthly = '',
  pricePrefix,
  priceSuffix,
  priceStrikethrough,
  sectionDescription,
  sectionHeading,
  image,
  bottomLink,
  dataTestId = 'PricingCardCTA-1'
}: PricingCardCTAProps) => {
  const { isPricingTest, setIsPricingTest } = usePageContext()

  useEffect(() => {
    // MKE-3840: September Pricing Test
    // We need a way for the header to know if this Ninetailed variant is active in order to display a banner.
    // Matching the badge text seems to be the simplest solution that will work consistently across environments.
    // This should only be active on the Pricing page
    if (
      typeof window !== 'undefined' &&
      window.location.pathname === '/pricing' &&
      badgeText.toLowerCase() === 'limited time offer' &&
      !isPricingTest
    ) {
      setIsPricingTest(true)
    }
  }, [badgeText, isPricingTest, setIsPricingTest])

  const convertSymbolsToWords = (text: string): string => {
    if (!text) return text

    // Replace symbols with words
    let converted = text.replace(/\//g, ' per ').replace(/\+/g, ' plus ')

    // Remove any double spaces
    converted = converted.replace(/\s+/g, ' ').trim()

    return converted
  }

  const renderImage = () => {
    if (image) {
      const query = ContentfulImageAPI.transformImageAvif({
        width: 900,
        extension: image.file?.contentType
      })
      const srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
        url: image.file?.url,
        widths: [900, 736, 393],
        extension: image.file?.contentType
      })
      const imgResolutions = {
        imageResolutions: [
          {
            borderRadius: 'Small',
            desktop: [
              {
                optimizedImages: {
                  src: image.file?.url,
                  srcsetAvif: srcsetWeb ? srcsetWeb : image.file?.url,
                  srcAvif: image.file?.url + query
                },
                url: image.file?.url,
                title: '',
                description: '',
                extension: image.file?.contentType
              }
            ]
          }
        ]
      }
      return (
        <div className='w-full'>
          <ImageResolutions images={imgResolutions} />
        </div>
      )
    }
  }

  const listItems: any[] = []
  if (Array.isArray(children)) {
    children.forEach((child: any) => {
      if (Array.isArray(child.listItems) && child.listItems.length > 0) {
        listItems.push({
          ...child.listItems[0],
          listStyles: child.listStyles
        })
      }
    })
  }

  return (
    <div
      className='bg-white mt-[26px] relative rounded-md border-card-light pricing-card'
      data-testid={dataTestId}
    >
      <div className='ds-grid gap-y-0 p-6'>
        <div className='col-span-4'>
          {badgeText && badgeColor && (
            <div className='mb-2'>
              <Badge badgeColor={badgeColor} text={badgeText} uppercase />
            </div>
          )}
          <div>
            {sectionHeading && (
              <Typography
                variation='h4'
                as='h4'
                classes='mb-[10px] sm:mb-4 !font-normal'
              >
                {sectionHeading}
              </Typography>
            )}
            {sectionDescription && (
              <Typography variation='default' as='p' classes='text-secondary'>
                {sectionDescription}
              </Typography>
            )}
          </div>
          <div className='mt-4 self-center'>
            {pricePrefix && (
              <Typography as='p' variation='default' classes='text-secondary'>
                {pricePrefix}
              </Typography>
            )}
            {priceStrikethrough && (
              <Typography
                as='p'
                variation='h4'
                classes='text-gray-50 line-through !font-normal'
              >
                {`<span>
                  <s>
                    <span className='sr-only'>Original price:</span>
                    ${priceStrikethrough}
                  </s>
                </span>`}
              </Typography>
            )}
            {packagePrice && (
              <p>
                <Typography
                  as='span'
                  classes='!text-[31px] !leading-[40px] !text-default !font-effra'
                  variation='default'
                >
                  {packagePrice}
                </Typography>
                {packagePriceMonthly && (
                  <Typography
                    as='span'
                    ariaLabel={convertSymbolsToWords(packagePriceMonthly)}
                    variation='default'
                    classes='text-secondary'
                  >
                    {packagePriceMonthly}
                  </Typography>
                )}
              </p>
            )}
            {priceSuffix && (
              <Typography
                ariaLabel={convertSymbolsToWords(priceSuffix)}
                as='p'
                variation='default'
                classes='text-secondary'
              >
                {priceSuffix}
              </Typography>
            )}
          </div>
          <div className='mt-4 sm:mt-6'>
            {ctaLabel && ctaLink && (
              <Button
                href={ctaLink}
                variation='primary'
                fontWeight={['font-semibold']}
                size='medium'
              >
                {ctaLabel}
              </Button>
            )}
          </div>
        </div>
        <div className='col-span-4 mt-4 sm:mt-0'>
          {listItems.length > 0 && (
            <StyledList
              listOnly
              sectionHeading=''
              sectionDescription=''
              listVariation='heading-only'
              listStyles='none'
              listItems={listItems}
            />
          )}
          {bottomLink && (
            <div className='mt-5 pt-2 border-t border-t-gray-30'>
              <TextLink
                description={`${sectionHeading} ${bottomLink.label}`}
                href={bottomLink.url}
                text={bottomLink.label}
                icon={Icon.linkIcons(bottomLink.icon)}
                classes='mt-2'
              />
            </div>
          )}
        </div>
        <div className='col-span-4 content-center mt-[31px] sm:mt-0 mb-5 sm:mb-0'>
          {image && renderImage()}
        </div>
      </div>
    </div>
  )
}

export default PricingCardCTA
